import { useState } from "react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { TokenReveal } from "../../components/TokenReveal";
import { ApiError, api } from "../../lib/api";
import type { CameraResponse, EdgeAgentResponse } from "../../lib/api";
import styles from "./AgentsPanel.module.css";
import {
  HardwareFields,
  type HardwareState,
  type HardwareValue,
  customHardwareState,
  emptyHardwareState,
  resolveHardware,
} from "./HardwareFields";
import sharedStyles from "./StoreDetail.module.css";
import { edgeAgentStatusVariant } from "./edgeAgentStatus";
import { kindAt, matchKindIndex } from "./edgeBoxKinds";

export type AgentsPanelProps = {
  storeId: string;
  agents: EdgeAgentResponse[];
  cameras: CameraResponse[];
  /** Re-fetch the store after a mutation so counts/status stay in sync. */
  onChanged: () => void;
};

const errorMessage = (e: unknown): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : "Something went wrong.";

type HardwarePickerProps = {
  initial: HardwareState;
  submitLabel: string;
  busy: boolean;
  onSubmit: (value: HardwareValue) => void;
  onCancel: () => void;
};

/** Shared edge-box picker for both Add and per-agent Edit. Wraps the controlled HardwareFields
 *  with submit/cancel; a fixed kind shows read-only description + capacity, "Custom" makes both
 *  editable. */
const HardwarePicker = ({
  initial,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: HardwarePickerProps) => {
  const [state, setState] = useState<HardwareState>(initial);
  const value = resolveHardware(state);

  return (
    <div className={styles.form}>
      <HardwareFields state={state} onChange={setState} busy={busy} />
      <div className={styles.formRow}>
        <Button
          size="sm"
          onClick={() => value && onSubmit(value)}
          disabled={busy || value === null}
        >
          {submitLabel}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const AgentsPanel = ({ storeId, agents, cameras, onChanged }: AgentsPanelProps) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const assignedCount = (agentId: string): number =>
    cameras.filter((c) => c.edge_agent_id === agentId).length;

  const toggle = async (agent: EdgeAgentResponse): Promise<void> => {
    setError(null);
    setBusyId(agent.id);
    try {
      if (agent.status === "disabled") {
        await api.enableEdgeAgent(storeId, agent.id);
      } else {
        await api.disableEdgeAgent(storeId, agent.id);
      }
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const rename = async (agent: EdgeAgentResponse): Promise<void> => {
    const next = window.prompt("Edge agent name", agent.name ?? "");
    if (next === null) return;
    const trimmed = next.trim();
    setError(null);
    setBusyId(agent.id);
    try {
      await api.updateEdgeAgent(storeId, agent.id, { name: trimmed === "" ? null : trimmed });
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (agent: EdgeAgentResponse): Promise<void> => {
    const label = agent.name ?? agent.hardware_model ?? agent.id.slice(0, 8);
    const ok = window.confirm(`Delete edge agent "${label}"? Its cameras return to inventory.`);
    if (!ok) return;
    setError(null);
    setBusyId(agent.id);
    try {
      await api.deleteEdgeAgent(storeId, agent.id);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const createAgent = async (value: HardwareValue): Promise<void> => {
    setError(null);
    setCreating(true);
    try {
      const provisioned = await api.createEdgeAgent(storeId, value);
      setNewKey(provisioned.api_key);
      setAddOpen(false);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const saveHardware = async (agent: EdgeAgentResponse, value: HardwareValue): Promise<void> => {
    setError(null);
    setBusyId(agent.id);
    try {
      await api.updateEdgeAgent(storeId, agent.id, value);
      setEditingId(null);
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Edge agents</span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setAddOpen(true)}
          disabled={addOpen || creating}
        >
          Add edge agent
        </Button>
      </div>

      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}

      {newKey && <TokenReveal token={newKey} label="New edge agent API key — shown once" />}

      {addOpen && (
        <HardwarePicker
          initial={emptyHardwareState()}
          submitLabel={creating ? "Creating…" : "Create"}
          busy={creating}
          onSubmit={createAgent}
          onCancel={() => setAddOpen(false)}
        />
      )}

      {agents.length === 0 ? (
        <div className={sharedStyles.stub}>
          No edge agents yet — add one above, then install it with the issued key. It will
          auto-discover and populate this store's cameras.
        </div>
      ) : (
        <div className={sharedStyles.nvrList}>
          {agents.map((agent) => {
            const count = assignedCount(agent.id);
            const full = count >= agent.allowed_number_of_cameras;
            const revoked = agent.status === "revoked";
            const kindIndex = matchKindIndex(agent.hardware_model);
            const editingCustom = kindAt(kindIndex).custom;
            return (
              <div key={agent.id} className={sharedStyles.agentRow}>
                <div>
                  <div className={sharedStyles.nvrName}>
                    {agent.name ?? agent.hardware_model ?? agent.agent_version ?? "edge agent"}
                  </div>
                  <div className={sharedStyles.nvrSub}>
                    <code>{agent.id.slice(0, 8)}</code> ·{" "}
                    <span className={full ? styles.capacityFull : styles.capacity}>
                      {count} / {agent.allowed_number_of_cameras} cameras
                    </span>{" "}
                    ·{" "}
                    {agent.last_seen_at
                      ? `last seen ${new Date(agent.last_seen_at).toLocaleString()}`
                      : "never seen"}
                  </div>
                  {editingId === agent.id && (
                    <HardwarePicker
                      initial={
                        editingCustom
                          ? customHardwareState(
                              agent.hardware_model ?? "",
                              agent.allowed_number_of_cameras,
                            )
                          : { index: kindIndex, customModel: "", customCapacity: "" }
                      }
                      submitLabel={busyId === agent.id ? "Saving…" : "Save hardware"}
                      busy={busyId === agent.id}
                      onSubmit={(value) => saveHardware(agent, value)}
                      onCancel={() => setEditingId(null)}
                    />
                  )}
                </div>
                <div className={styles.right}>
                  <Badge variant={edgeAgentStatusVariant(agent.status)}>{agent.status}</Badge>
                  {!revoked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingId(editingId === agent.id ? null : agent.id)}
                      disabled={busyId === agent.id}
                    >
                      Hardware
                    </Button>
                  )}
                  {!revoked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => rename(agent)}
                      disabled={busyId === agent.id}
                    >
                      Rename
                    </Button>
                  )}
                  {agent.status !== "online" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(agent)}
                      disabled={busyId === agent.id}
                    >
                      Delete
                    </Button>
                  )}
                  {revoked ? (
                    <span className={styles.revoked}>decommissioned</span>
                  ) : agent.status === "disabled" ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => toggle(agent)}
                      disabled={busyId === agent.id}
                    >
                      Enable
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggle(agent)}
                      disabled={busyId === agent.id}
                    >
                      Disable
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
